import _ from 'lodash'
import { PayloadAction, createAction, createSelector, createSlice } from "@reduxjs/toolkit"
import { ImportWriteMode, SecondaryCode, UnfilteredIcd } from '@fusion-advantage/backcompat'
import {
	AssertionStatus,
	AuditDecision,
	DiagnosisReviewItem,
	DiagnosisReviewItemPresence,
	DiagnosisSource,
	diagnosisReviewItemPendingClinicalInfoQuery,
	diagnosisReviewItemIsPendingAuditComment,
	diagnosisReviewItemPresence,
	diagnosisReviewItemRequiresAuditDecision,
	newDiagnosisReviewItem,
	diagnosisReviewItemShouldCleanupAuditDecision,
	isConceptStatus,
	Comment,
	IndexedModel,
	diagnosisReviewItemPostUpdate,
	OncologyReviewConcept,
	NeurologicWeaknessConcept,
	contraStatus,
	ContraStatus,
	UlcerReviewConcept,
} from '@fusion-advantage/formlib'
import { shallowEqual } from 'react-redux'
import { FormThunk } from './formStore'
import { selectConcept } from './selectConcept'
import { selectConceptStatus } from './selectConceptStatus2'
import { FormRootState } from './formStore'
import { addComment, updateComment } from '../lib/modelstate/CommentState'
import { selectExport } from './selectForm'
import { selectPrimaryTumorStatusMatch, selectMetastaticSiteStatusMatch, selectCarcinoidMatch } from './selectOncology'
import { selectCombinedIcdDesc } from './selectNeurologicWeakness'
import { selectDrugIcdsByCode } from './formModelSlice'

// internal to this file, do not call from outside!
const internalSetDiagnosisItems = createAction<DiagnosisReviewItem[]>('diagnosisReview/internalSetDiagnosisItems')

export interface DiagnosisReviewItemByIdxUpdate {
	idx: number
	item: DiagnosisReviewItem
}

interface DiagnosisReviewItemUpdate {
	cid?: string
	source: DiagnosisSource|null
	adhocTitle?: string
	dxHistoryTitle?: string
	dxOnAssessment: boolean
	dxEvidence: boolean
	conceptTitle?: string
	rxEvidence: boolean
	indicationTitle?: string
	committed: boolean
}

const newDiagnosisReviewItemUpdate = (): DiagnosisReviewItemUpdate => ({
	source: null,
	dxOnAssessment: false,
	dxEvidence: false,
	rxEvidence: false,
	committed: false,
})

export interface DiagnosisReviewItemUpdateByIcd {
	[icd10: string]: DiagnosisReviewItemUpdate,
}

type DiagnosisCommentType = 'comments'|'auditComments'

interface DiagnosisCommentGroupRef {
	itemIdx: number,
	itemAuditNextStatus?: AuditDecision,
	commentType: DiagnosisCommentType
}

interface DiagnosisCommentRef extends DiagnosisCommentGroupRef {
	commentIdx: number|'new'
	defaultContent?: string
}

export interface DiagnosisReviewSliceState {
	adhocDx: UnfilteredIcd[]
	models: DiagnosisReviewItem[]
	secondaryCodes: null|SecondaryCode[]
	secondaryCodesLoading: boolean
	displayingComments: DiagnosisCommentGroupRef|null
	editingComment: DiagnosisCommentRef|null
	resultingDiagnosis: null|SecondaryCode[]
}

const initialState: DiagnosisReviewSliceState = {
	adhocDx: [],
	models: [],
	secondaryCodes: null,
	secondaryCodesLoading: false,
	displayingComments: null,
	editingComment: null,
	resultingDiagnosis: null,
}

const diagnosisReviewSlice = createSlice({
	name: 'diagnosisReview',
	initialState,
	reducers: {
		setInitialAdhocDx(state, action: PayloadAction<UnfilteredIcd[]>) {
			state.adhocDx = action.payload
		},
		pushAdhocDxs(state, action: PayloadAction<UnfilteredIcd[]>) {
			state.adhocDx.push(...action.payload)
		},
		removeAdhocDxIcd(state, action: PayloadAction<string>) {
			state.adhocDx = state.adhocDx.filter(dx => dx.Icd10Code !== action.payload)
		},
		setInitialDiagnosisReview(state, action: PayloadAction<DiagnosisReviewItem[]>) {
			state.models = action.payload
		},
		setSecondaryCodes(state, action: PayloadAction<null|SecondaryCode[]>) {
			state.secondaryCodes = action.payload
		},
		setSecondaryCodesLoading(state, action: PayloadAction<boolean>) {
			state.secondaryCodesLoading = action.payload
		},
		setDiagnosisReviewDisplayingComments(state, action: PayloadAction<DiagnosisCommentGroupRef|null>) {
			state.displayingComments = action.payload
		},
		setDiagnosisReviewEditingComments(state, action: PayloadAction<DiagnosisCommentRef|null>) {
			state.editingComment = action.payload
		},
		cleanupDiagnosisReviewAuditDecisions(state) {
			for(let model of state.models) {
				model.prevSubmission = undefined
				if(diagnosisReviewItemShouldCleanupAuditDecision(model) && model.auditDecision !== null) {
					model.auditDecision = null
				}
			}
		},
		setResultingDiagnosis(state, action: PayloadAction<null|SecondaryCode[]>) {
			state.resultingDiagnosis = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(internalSetDiagnosisItems, (state, action) => {
			state.models = action.payload
		})
	},
})

export const {
	setInitialAdhocDx,
	pushAdhocDxs,
	removeAdhocDxIcd,
	setInitialDiagnosisReview,
	setSecondaryCodes,
	setSecondaryCodesLoading,
	setDiagnosisReviewDisplayingComments,
	setDiagnosisReviewEditingComments,
	cleanupDiagnosisReviewAuditDecisions,
	setResultingDiagnosis,
} = diagnosisReviewSlice.actions
export default diagnosisReviewSlice.reducer

export const selectDiagnosisReviewIsWritable = createSelector(
	(state: FormRootState) => state.render.form?.writeMode,
	writeMode => {
		return writeMode === ImportWriteMode.WriteAll || writeMode === ImportWriteMode.WriteAuditReview
	}
)

// an update by a user within the DiagnosisReview UI (clinician or audit);
// supports Thad's approach of submitting a replacement version of
// the whole model in question
export const updateDiagnosisItemByIdx = ({idx, item}: DiagnosisReviewItemByIdxUpdate): FormThunk<void> => (dispatch, getState) => {
	const models = [ ...getState().diagnosisReview.models ]
	const prevItem = models[idx]
	let nextItem = _.cloneDeep(item)
	nextItem = diagnosisReviewItemPostUpdate(prevItem, nextItem, selectDrugIcdsByCode(getState()))
	models[idx] = nextItem
	dispatch(internalSetDiagnosisItems(models))
	dispatch(setSecondaryCodes(null))
}

export const updateDiagnosisItemsFromInputs = (dxCids: string[]): FormThunk<void> => (dispatch, getState) => {
	const updateByIcd: DiagnosisReviewItemUpdateByIcd = {}
	const updateForIcd = (icd: string) => {
		if(!updateByIcd[icd]) {
			updateByIcd[icd] = newDiagnosisReviewItemUpdate()
		}
		return updateByIcd[icd]
	}
	const state = getState()
	_.forEach(getState().diagnosisReview.adhocDx, dx => {
		const update = updateForIcd(dx.Icd10Code)
		update.adhocTitle = dx.Description
		update.source = DiagnosisSource.Adhoc
	})
	_.forEach(getState().formModel.claimHistory, historyItem => {
		const update = updateForIcd(historyItem.DiagnosisCode)
		update.dxHistoryTitle = historyItem.DiagnosisName
		update.source = DiagnosisSource.DxHistory
	})
	_.forEach(dxCids, cid => {
		const concept = selectConcept(state, cid)
		if(!concept?.icd10) {
			return
		}
		const update = updateForIcd(concept.icd10)
		update.cid = concept.cid
		update.dxOnAssessment = true
		if(concept.title) {
			update.conceptTitle = concept.title
		}
		const status = selectConceptStatus(state, cid)
		if(isConceptStatus(status) && status.value) {
			update.dxEvidence = true
		}
	})
	_.forEach(getState().drug.models, rxItem => {
		// [ISSUE-1213] Med Recon: only drugs that are In Use = Yes 
		// should be RxEvidence for DiagnosisReview table
		if(rxItem.InUse !== true) {
			return
		}
		_.forEach(rxItem.Indications, indication => {
			// [ISSUE-637] Dan has plugged in fake indications for
			// "Preventative" and "Unknown" into the database side; we want to
			// make sure these do not show up on the Assessment table
			if(indication.Icd10Code.toUpperCase().startsWith('FAKE')) {
				return
			}
			const update = updateForIcd(indication.Icd10Code)
			update.rxEvidence = true
			update.indicationTitle = indication.Description
		})
	})
	const updateForNeurology = (icd: string, description: string) => {
		const update = updateForIcd(icd)
		update.dxEvidence = true
		update.dxOnAssessment = true
		update.indicationTitle = description
		update.cid = NeurologicWeaknessConcept.cid
	}
	_.forEach(getState().neurologicWeaknessReview.models, item => {
		if(item.code2) {
			updateForNeurology(item.code1, selectCombinedIcdDesc(state, item.code1, true))
			updateForNeurology(item.code2, selectCombinedIcdDesc(state, item.code2, true))
		}
		else {
			updateForNeurology(item.code1, selectCombinedIcdDesc(state, item.code1, false))
		}
	})
	const updateForOncologyIcd = (icd: string, icdDesc: string) => {
		const update = updateForIcd(icd)
		update.cid = OncologyReviewConcept.cid
		update.indicationTitle = icdDesc
		update.dxOnAssessment = true
		update.dxEvidence = true
	}
	_.forEach(getState().formModel.oncologyReview, item => {
		const matches = [
			selectPrimaryTumorStatusMatch(state, item.tumor, item.treatmentStatus),
			selectMetastaticSiteStatusMatch(state, item.metastaticSite, item.metastaticSiteStatus),
		]
		const carcinoidMatch = selectCarcinoidMatch(state, item.tumor, item.metastaticSite, item.treatmentStatus)
		if (carcinoidMatch) {
			if (carcinoidMatch.ICD10_1_Eval) {
				updateForOncologyIcd(carcinoidMatch.ICD10_1_Eval, carcinoidMatch.ICD10_1_Description)
			}
			if (carcinoidMatch.ICD10_2_Eval) {
				updateForOncologyIcd(carcinoidMatch.ICD10_2_Eval, carcinoidMatch.ICD10_2_Description ?? 'Unknown Description')
			}
		} else {
			matches.forEach(match => {
				if (match) {
					updateForOncologyIcd(match.ICD10_1_Eval, match.ICD10_1_Description)
				}
			})
		}
	})
	const updateForUlcerIcd = (icd: string, icdDesc: string) => {
		const update = updateForIcd(icd)
		update.cid = UlcerReviewConcept.cid
		update.indicationTitle = icdDesc
		update.dxOnAssessment = true
		update.dxEvidence = true
	}
	_.forEach(getState().formModel.ulcerReview, item => {
		if(item.icdCode) {
			updateForUlcerIcd(item.icdCode, [item.ulcerType, item.sites, item.stage].join(' - '))
		}
	})
	dispatch(upsertDiagnosisItems(updateByIcd))
}

// updates generated from the form outside the DiagnosisReview UI
export const upsertDiagnosisItems = (updateByIcd: DiagnosisReviewItemUpdateByIcd): FormThunk<void> => (dispatch, getState) => {
	const applyUpdateToItem = (item: DiagnosisReviewItem, update: DiagnosisReviewItemUpdate): DiagnosisReviewItem => ({
		...item,
		title: update.conceptTitle ?? update.indicationTitle ?? update.adhocTitle ?? update.dxHistoryTitle ?? `Unknown Description (${item.icd10Code})`,
		source: update.source ?? null,
		dxOnAssessment: update.dxOnAssessment,
		dxEvidence: update.dxEvidence,
		rxEvidence: update.rxEvidence,
	})
	const reviewIdxToRemove: number[] = []
	let models = getState().diagnosisReview.models.map((reviewItem, reviewItemIdx) => {
		let updatedItem = { ...reviewItem }
		// [ISSUE-670] When indications with certain ICD codes are
		// dropped in the drug scanner, no updateByIcd is generated
		// in updateDiagnosisItemsFromForm() since there is no other
		// reference to that ICD elsewhere.  So we create blank
		// DiagnosisReviewItemUpdate objects here that cause
		// diagnosisReviewItemPresence to drop these otherwise
		// unsupported items.
		const update = updateByIcd[reviewItem.icd10Code] ?? newDiagnosisReviewItemUpdate()
		updatedItem = applyUpdateToItem(reviewItem, update)
		update.committed = true
		updatedItem = diagnosisReviewItemPostUpdate(reviewItem, updatedItem, selectDrugIcdsByCode(getState()))
		if(diagnosisReviewItemPresence(updatedItem) === DiagnosisReviewItemPresence.Drop) {
			reviewIdxToRemove.push(reviewItemIdx)
		}
		return shallowEqual(reviewItem, updatedItem) ? reviewItem : updatedItem
	})
	models = models.filter((m, idx) => !reviewIdxToRemove.includes(idx))
	// review of updateByIcds from updateDiagnosisItemsFromInputs()
	// that don't have a matching DiagnosisItem (adding Items)
	_.forEach(updateByIcd, (update, icd10Code) => {
		if(!update.committed) {
			const prevItem = newDiagnosisReviewItem(update.cid ?? null, icd10Code)
			let nextItem = applyUpdateToItem(prevItem, update)
			// hmm, when would a thunk update not should be on the list?
			if(diagnosisReviewItemPresence(nextItem) !== DiagnosisReviewItemPresence.Drop) {
				nextItem = diagnosisReviewItemPostUpdate(prevItem, nextItem, selectDrugIcdsByCode(getState()))
				models.push(nextItem)
			}
			update.committed = true
		}
	})
	dispatch(internalSetDiagnosisItems(models))
}

export const loadInitialDiagnosisReview = (models: DiagnosisReviewItem[]): FormThunk<void> => (dispatch) => {
	for(let model of models) {
		if(!model.prevSubmission) {
			model.prevSubmission = {
				auditDecision: model.auditDecision,
				auditDecisionUpdatedAt: model.auditDecisionUpdatedAt,
			}
		}
	}
	dispatch(setInitialDiagnosisReview(models))
	return
}

export interface DiagnosisReviewAuditValidation {
	clinicalPending: number
	decisionsPending: number
	rejectCommentsPending: number
	queriesPending: number
	queryCommentsPending: number
	secondaryCodesHasRun: boolean
	secondaryCodesPending: number
}

export const selectDiagnosisReviewAuditValidation = createSelector(
	(state: FormRootState) => state.diagnosisReview.models,
	(state: FormRootState) => state.diagnosisReview.secondaryCodes,
	(state: FormRootState) => selectExport(state)?.Form.Encounter.EncounterAppLastStatusDT,
	(models, secondaryCodes, encounterAppLastStatusDT): DiagnosisReviewAuditValidation => {
		const clinicalPending = models.filter(x => diagnosisReviewItemPendingClinicalInfoQuery(x)).length
		const requiringDecision = models.filter(x => diagnosisReviewItemRequiresAuditDecision(x))
		const decisionsPending = requiringDecision.filter(x => !x.auditDecision).length
		const rejections = models.filter(x => x.auditDecision === AuditDecision.Rejected)
		const rejectCommentsPending = rejections.filter(x => diagnosisReviewItemIsPendingAuditComment(x, encounterAppLastStatusDT)).length
		const queries = models.filter(x => x.auditDecision === AuditDecision.Question)
		const queriesPending = queries.length
		const queryCommentsPending = queries.filter(x => diagnosisReviewItemIsPendingAuditComment(x, encounterAppLastStatusDT)).length
		const secondaryCodesHasRun = _.isArray(secondaryCodes)
		const secondaryCodesPending = (secondaryCodes || []).filter(x => contraStatus(x) === ContraStatus.Conflict).length
		return {
			clinicalPending,
			decisionsPending,
			rejectCommentsPending,
			queriesPending,
			queryCommentsPending,
			secondaryCodesHasRun,
			secondaryCodesPending,
		}
	}
)

export interface DiagnosisReviewClinicianReviewValidation {
	queryCommentsPending: number
}

export const selectDiagnosisReviewClinicianReviewValidation = createSelector(
	(state: FormRootState) => state.diagnosisReview.models,
	(state: FormRootState) => selectExport(state)?.Form.Encounter.EncounterAppLastStatusDT,
	(models, encounterAppLastStatusDT): DiagnosisReviewClinicianReviewValidation => {
		const queries = models.filter(x => x.auditDecision === AuditDecision.Question)
		const queryCommentsPending = queries.filter(x => diagnosisReviewItemIsPendingAuditComment(x, encounterAppLastStatusDT)).length
		return {
			queryCommentsPending,
		}
	}
)

export const selectDiagnosisReviewDisplayingComments = createSelector(
	(state: FormRootState) => state.diagnosisReview.models,
	(state: FormRootState) => state.diagnosisReview.displayingComments,
	(models, displayingCommentsRef): Comment[]|null => {
		if(!displayingCommentsRef) {
			return null
		}
		return models[displayingCommentsRef.itemIdx][displayingCommentsRef.commentType]
	}
)

export const selectDiagnosisReviewEditingComment = createSelector(
	(state: FormRootState) => state.diagnosisReview.models,
	(state: FormRootState) => state.diagnosisReview.editingComment,
	(models, commentRef) => {
		if(!commentRef || commentRef.commentIdx === 'new') {
			return null
		}
		return models[commentRef.itemIdx][commentRef.commentType][commentRef.commentIdx]
	}
)

export const commitDiagnosisReviewComment = (message: string): FormThunk<void> => (dispatch, getState) => {
	const state = getState()
	const commentRef = state.diagnosisReview.editingComment
	if(!commentRef) {
		alert('Error: no comment selected for update')
		return
	}
	const { itemIdx, commentType, commentIdx } = commentRef
	let item = state.diagnosisReview.models[itemIdx]
	let comments
	if(commentIdx !== 'new') {
		comments = dispatch(updateComment(item[commentType], commentIdx, message))
	} else {
		comments = dispatch(addComment(item[commentType], message))
	}
	if(!comments) {
		alert('Error on comment save; please contact support.')
		return
	}
	item = {
		...item,
		[commentType]: comments,
	}
	if(commentRef.commentIdx === 'new' && commentRef.commentType === 'auditComments') {
		if(commentRef.itemAuditNextStatus) {
			item.auditDecision = commentRef.itemAuditNextStatus!
		}
	}
	dispatch(updateDiagnosisItemByIdx({
		idx: itemIdx,
		item,
	}))
}

export const editDiagnosisReviewDisplayingComment = (commentIdx: number|'new'): FormThunk<void> => (dispatch, getState) => {
	const state = getState()
	const commentRef = state.diagnosisReview.displayingComments
	if(!commentRef) {
		return
	}
	const { itemIdx, commentType } = commentRef
	dispatch(setDiagnosisReviewEditingComments({
		itemIdx,
		commentType,
		commentIdx,
	}))
}

export const selectDiagnosisReviewSortedModels = createSelector(
	(state: FormRootState) => state.diagnosisReview.models,
	stateModels => {
		let models = _.map(stateModels, (model, stateIdx): IndexedModel<DiagnosisReviewItem> => ({ model, stateIdx }))
		models = _.sortBy(models, ({model}) => {
			// [ISSUE-423] sort by asserted|history|indeterminate, then ICD code
			let statusOrder: string
			switch(model.status) {
				case AssertionStatus.Asserted:
					statusOrder = '1'
					break
				case AssertionStatus.HistoryOf:
					statusOrder = '2'
					break
				case AssertionStatus.Indeterminate:
					statusOrder = '3'
					break
				default:
					statusOrder = '4'
			}
			return statusOrder + '|' + model.icd10Code
		})
		return models
	}
)
